import React from "react";
import { IClinician, IIntakeAvailability } from "intake_shared/src/types";
import { DepartmentCache } from "../../../../../store/cache/requested_service";
import { Dialog } from "../../../bits/Dialog/Dialog";
import Alert from "../../../bits/Alert/Alert";
import { TextArea } from "../../../bits/TextArea/TextArea";
import moment from "../../../../../utils/app_moment";
import { DateFormats } from "intake_shared/src/types";
import { findLabelByPracticeId } from "../../../../../utils/utils";
import styles from "./SelectIntakeSessionEvaluatorDialog.module.scss";

interface SelectIntakeSessionEvaluatorDialogProps {
    open: boolean;
    onClose: () => void;
    supervisors: IClinician[];
    intakeAvailabilityBySupervisor: {
        [supervisorId: string]: IIntakeAvailability[];
    };
    selectedClinician: IClinician;
    defaultSupervisorId: string;
    bookingPracticeId?: string;
    allDepartmentsAndServices: DepartmentCache;
    onSelect: (supervisorId: string, intakeSession: IIntakeAvailability) => void;
    showRescheduleReason?: boolean;
    rescheduleReason?: string;
    onRescheduleReasonChange?: (value: string) => void;
    viewFrom?: "appt_held" | "appt_confirmed";
}

export default function SelectIntakeSessionEvaluatorDialog({
    open,
    onClose,
    supervisors,
    intakeAvailabilityBySupervisor,
    selectedClinician,
    defaultSupervisorId,
    bookingPracticeId,
    allDepartmentsAndServices,
    onSelect,
    showRescheduleReason = false,
    rescheduleReason = "",
    onRescheduleReasonChange,
    viewFrom,
}: SelectIntakeSessionEvaluatorDialogProps) {
    const [selectedSlotInfo, setSelectedSlotInfo] = React.useState<{
        supervisorId: string;
        slot: IIntakeAvailability;
    } | null>(null);
    const [showRescheduleEmailNoticeDialog, setShowRescheduleEmailNoticeDialog] =
        React.useState(false);
    const defaultPracticeId = process.env.REACT_APP_MANTLE_PRACTICE_ID ?? "";

    const handleFinalConfirm = () => {
        if (!selectedSlotInfo) return;
        onSelect(selectedSlotInfo.supervisorId, selectedSlotInfo.slot);
        onClose();
    };

    const handlePrimaryAction = () => {
        if (!selectedSlotInfo) return;
        if (showRescheduleReason && viewFrom === "appt_confirmed") {
            setShowRescheduleEmailNoticeDialog(true);
            return;
        }
        handleFinalConfirm();
    };

    const clinicianSupervisorIds = React.useMemo(() => {
        const ids = new Set<string>();
        if (selectedClinician?.supervisor_id) {
            ids.add(selectedClinician.supervisor_id);
        }
        (selectedClinician?.secondary_supervisor_ids || []).forEach((pair) => {
            if (pair?.supervisor_id) {
                ids.add(pair.supervisor_id);
            }
        });
        return ids;
    }, [selectedClinician]);

    const isClinicianSupervisorSlot = React.useCallback(
        (supervisorId: string) => {
            if (!clinicianSupervisorIds.size) return false;
            if (clinicianSupervisorIds.has(supervisorId)) return true;
            const supervisor = supervisors.find(
                (s) => s.email === supervisorId || s.id === supervisorId
            );
            if (!supervisor) return false;
            return Boolean(
                (supervisor.id && clinicianSupervisorIds.has(supervisor.id)) ||
                    (supervisor.email &&
                        clinicianSupervisorIds.has(supervisor.email))
            );
        },
        [clinicianSupervisorIds, supervisors]
    );

    const slotSupervisorMatchesDefault = React.useCallback(
        (slotSupervisorEmail: string, defaultId?: string) => {
            if (!defaultId) return false;
            if (defaultId === slotSupervisorEmail) return true;
            const sup = supervisors.find(
                (s) => s.id === defaultId || s.email === defaultId
            );
            return Boolean(sup && sup.email === slotSupervisorEmail);
        },
        [supervisors]
    );

    const supervisorPracticeIdsById = React.useMemo(() => {
        const map: { [supervisorId: string]: string[] } = {};
        supervisors.forEach((sup) => {
            const practiceIds = Array.from(
                new Set(
                    (sup.departments || [])
                        .map(
                            (department) =>
                                allDepartmentsAndServices[department]?.practice_id ||
                                ""
                        )
                        .filter(Boolean)
                )
            );
            if (sup.email) map[sup.email] = practiceIds;
            if (sup.id) map[sup.id] = practiceIds;
        });
        return map;
    }, [supervisors, allDepartmentsAndServices]);

    const getIntakeSlotPracticeLabel = (slot: IIntakeAvailability) => {
        if (!slot.practice_id) {
            return "Practice not set";
        }
        return (
            findLabelByPracticeId(slot.practice_id, allDepartmentsAndServices) ||
            "Unknown practice"
        );
    };

    const allSlots = React.useMemo(() => {
        const combined: { supervisorId: string; slot: IIntakeAvailability }[] = [];

        supervisors.forEach((supervisor) => {
            const supervisorId = supervisor.email;
            const slots = intakeAvailabilityBySupervisor[supervisorId] || [];
            slots.forEach((slot) => {
                if (bookingPracticeId) {
                    const supervisorPracticeIds =
                        supervisorPracticeIdsById[supervisorId] || [];
                    const practiceMatches =
                        slot.practice_id === bookingPracticeId ||
                        (!slot.practice_id &&
                            ((defaultPracticeId &&
                                bookingPracticeId === defaultPracticeId) ||
                                supervisorPracticeIds.includes(bookingPracticeId)));
                    if (!practiceMatches) {
                        return;
                    }
                }
                combined.push({ supervisorId, slot });
            });
        });

        return combined.sort((a, b) => {
            const aIsSupervisor = isClinicianSupervisorSlot(a.supervisorId);
            const bIsSupervisor = isClinicianSupervisorSlot(b.supervisorId);
            if (aIsSupervisor !== bIsSupervisor) {
                return aIsSupervisor ? -1 : 1;
            }
            const aTime = moment(a.slot.start_time);
            const bTime = moment(b.slot.start_time);
            return aTime.diff(bTime);
        });
    }, [
        supervisors,
        intakeAvailabilityBySupervisor,
        isClinicianSupervisorSlot,
        bookingPracticeId,
        defaultPracticeId,
        supervisorPracticeIdsById,
    ]);

    React.useEffect(() => {
        if (!open) {
            setSelectedSlotInfo(null);
            setShowRescheduleEmailNoticeDialog(false);
            return;
        }
        if (defaultSupervisorId) {
            const match = allSlots.find(({ supervisorId }) =>
                slotSupervisorMatchesDefault(supervisorId, defaultSupervisorId)
            );
            setSelectedSlotInfo(
                match ? { supervisorId: match.supervisorId, slot: match.slot } : null
            );
        } else {
            setSelectedSlotInfo(null);
        }
    }, [open, defaultSupervisorId, allSlots, slotSupervisorMatchesDefault]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                title="Choose Supervisor"
                onSave={handlePrimaryAction}
                onCancel={onClose}
                saveText={showRescheduleReason ? "Continue" : "Confirm"}
                cancelText="Cancel"
                isSubmitBtnDisabled={
                    !selectedSlotInfo ||
                    (showRescheduleReason && !rescheduleReason.trim())
                }
            >
                <div className={styles.evaluatorSelection}>
                    <Alert type="warning" className={styles.requiredNote}>
                        This appointment requires an Intake session
                    </Alert>
                    <div className={styles.availabilitySection}>
                        <p className={styles.availabilityHeader}>
                            Available intake sessions:
                        </p>
                        <div className={styles.availabilitySlots}>
                            {allSlots.length === 0 ? (
                                <p className={styles.noSlots}>
                                    No available intake session slots
                                </p>
                            ) : (
                                <div className={styles.slotsContainer}>
                                    {allSlots.map(({ supervisorId, slot }) => {
                                        const startTime = moment(slot.start_time);
                                        const slotLabel = `${startTime.format(
                                            "ddd, MMM Do"
                                        )} - ${startTime.format(
                                            DateFormats.HOUR_MIN_FORMAT
                                        )}`;

                                        const isSelected =
                                            selectedSlotInfo?.slot.id === slot.id &&
                                            selectedSlotInfo?.supervisorId ===
                                                supervisorId;
                                        const isClinicianSupervisor =
                                            isClinicianSupervisorSlot(supervisorId);
                                        const practiceLabel =
                                            getIntakeSlotPracticeLabel(slot);

                                        return (
                                            <button
                                                key={`${supervisorId}-${slot.id}`}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedSlotInfo(null);
                                                    } else {
                                                        setSelectedSlotInfo({
                                                            supervisorId,
                                                            slot,
                                                        });
                                                    }
                                                }}
                                                className={`${styles.timeSlot} ${
                                                    isSelected
                                                        ? styles.selectedSlot
                                                        : ""
                                                } ${
                                                    isClinicianSupervisor
                                                        ? styles.clinicianSupervisorSlot
                                                        : ""
                                                }`}
                                                aria-pressed={isSelected}
                                            >
                                                {slotLabel}
                                                <span
                                                    className={styles.supervisorName}
                                                >
                                                    ({slot.clinician_display_name} -{" "}
                                                    {practiceLabel})
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {showRescheduleReason && (
                            <div>
                                <TextArea
                                    label="Reason for rescheduling"
                                    value={rescheduleReason}
                                    onChange={(e) =>
                                        onRescheduleReasonChange?.(e.target.value)
                                    }
                                    placeholder="Enter reason for rescheduling"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            <Dialog
                open={showRescheduleEmailNoticeDialog}
                onClose={() => setShowRescheduleEmailNoticeDialog(false)}
                title="Confirm Reschedule"
                onSave={() => {
                    setShowRescheduleEmailNoticeDialog(false);
                    handleFinalConfirm();
                }}
                onCancel={() => setShowRescheduleEmailNoticeDialog(false)}
                saveText="Confirm"
                cancelText="Cancel"
            >
                Since this session is being rescheduled, we will send a new email
                with updated details to both the client and clinician. Are you sure
                you want to reschedule this session?
            </Dialog>
        </>
    );
}
